import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import FieldData from "@/components/FieldData";
import { Plus, Trash, MagnifyingGlass, ClockCounterClockwise, WhatsappLogo, Phone, MapPin, Paperclip, FileArrowDown, UploadSimple, Camera, Kanban, Rows, UserPlus, PencilSimple, DotsThreeVertical, Printer, EnvelopeSimple, Sparkle, Eye, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER, JOB_STATUS_COLORS, PROPERTY_TYPES, ROLE_LABELS, JOB_PURPOSES, inr, bankKey } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import JobTimeline from "@/components/JobTimeline";
import PhotoGallery from "@/components/PhotoGallery";
import ValuationDetailsForm, { withDerived } from "@/components/ValuationDetailsForm";
import GisOverlayStudio from "@/components/GisOverlayStudio";
import RateModelTab from "@/components/RateModelTab";
import DocumentsTab from "@/components/DocumentsTab";
import DocsChecklistTab from "@/components/DocsChecklistTab";
import DeviationSignoff from "@/components/DeviationSignoff";
import ComparablesMap, { geocodeAddress } from "@/components/ComparablesMap";
import SitePhotosManager from "@/components/SitePhotosManager";
import { shareWhatsApp } from "@/lib/whatsapp";
import { openValuationReport, emailValuationReport, releaseReportAndInvoice, processReport } from "@/lib/report";
import { DatePicker } from "@/components/DatePicker";

// All 22 Annexure-I declarations default to "Yes" so new jobs start fully answered.
const DECL_YES = (() => { const o = {}; for (let i = 1; i <= 22; i++) o[`decl_${i}`] = "Yes"; return o; })();
const EMPTY = { bank_name: "", branch_name: "", applicant_name: "", property_type: "Residential", property_address: "", property_location: "", contact_mobile: "", loan_amount: "", fee: "", field_executive_id: "none", report_writer_id: "none", status: "enquiry", deadline: "", notes: "", purpose: "Mortgage", bank_reference_no: "", site_visit_date: "", tat_days: "", client_id: null, report_format_id: "", valuation_details: { ...DECL_YES } };

// Annexure-I "Background Information & Valuer Comments" particulars (mirrors backend report_annexures.DECLARATION_PARTICULARS).
const BG_PARTICULARS = [
  "Background information of the asset being valued",
  "Purpose of valuation and appointing authority",
  "Identity of the valuer and any other experts involved in the valuation",
  "Disclosure of valuer interest / conflict, if any",
  "Date of appointment, valuation date and date of report",
  "Inspections and / or investigations undertaken",
  "Nature and sources of the information used or relied upon",
  "Procedures adopted in carrying out the valuation and valuation standards followed",
  "Restrictions on use of the report, if any",
  "Major factors that were taken into account during the valuation",
  "Caveats, limitations and disclaimers to the extent they explain or elucidate the limitations faced by the valuer",
];

// Distance (km) and compass direction between two lat/long points — mirrors backend haversine/compass.
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
const compassDir = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
  const brng = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  return ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(brng / 45) % 8];
};

// Annexure-I DECLARATION-CUM-UNDERTAKING items (mirrors backend report_annexures.DECLARATION_UNDERTAKING).
const DECL_UNDERTAKINGS = [
  "I have personally inspected the property on the date of inspection mentioned in the report.",
  "I have no direct or indirect interest in the property valued.",
  "The information furnished in the report is true and correct to the best of my knowledge and belief.",
  "I have made an impartial and true valuation of the property.",
  "I have not been convicted of any offence and sentenced to a term of imprisonment.",
  "I have not been found guilty of misconduct in my professional capacity.",
  "I have read the Handbook on Policy, Standards and Procedure for Real Estate Valuation, 2011 of the IBA and this report is in conformity with the standards enshrined therein.",
  "I have read the International Valuation Standards (IVS) and the report is in conformity with the standards as enshrined in the IVS to the extent possible.",
  "The analysis and conclusions are limited only by the reported assumptions and conditions and the personal, unbiased professional analysis, opinion and conclusion.",
  "I have no present or prospective interest in the property that is the subject of this report, and no personal interest with respect to the parties involved.",
  "I have no bias with respect to the property that is the subject of this report or to the parties involved with this assignment.",
  "My engagement in this assignment was not contingent upon developing or reporting predetermined results.",
  "My compensation for completing this assignment is not contingent upon the development or reporting of a predetermined value or direction in value that favours the cause of the client.",
  "My analyses, opinions and conclusions were developed, and this report has been prepared, in conformity with the applicable regulations / guidelines.",
  "I have not exchanged any consideration, other than the professional fee, for carrying out this valuation.",
  "I abide by the Model Code of Conduct for the empanelment of valuer in the Bank (enclosed as Annexure-II).",
  "I am registered under Section 34 AB of the Wealth Tax Act, 1957 / registered as a valuer with IBBI.",
  "I undertake to keep the information confidential and not to disclose it to anyone without the written consent of the bank.",
  "The fair market value arrived at is based on the prevailing market condition as on the date of valuation and may change with time.",
  "This report is valid for a period of six months from the date of valuation for the stated purpose.",
  "I am the proprietor / partner / authorised official of the firm who is competent to sign this valuation report.",
  "Further, I hereby provide the following information / declaration as required by the bank.",
];
const LAYOUT_LABELS = { open_land: "Open Land", land_building: "Land & Building", industrial_land: "Industrial Land", flat: "Flats" };

const relTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); return `${d}d ago`;
};
const absTime = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};


export default function Jobs() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [releasing, setReleasing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [reportProcessed, setReportProcessed] = useState(false);
  const [cmpRadius, setCmpRadius] = useState(2);
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [bankDirectory, setBankDirectory] = useState([]);
  const [reportFormats, setReportFormats] = useState([]);
  const [bankTpls, setBankTpls] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bankFilter, setBankFilter] = useState("all");
  const [valuerFilter, setValuerFilter] = useState("all");
  const [ptFilter, setPtFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [layout, setLayout] = useState("list");
  const [detail, setDetail] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [notePhoto, setNotePhoto] = useState(null);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [baseUpdatedAt, setBaseUpdatedAt] = useState(null);
  const [reworkJob, setReworkJob] = useState(null);
  const [reworkNote, setReworkNote] = useState("");
  const [reworkInfo, setReworkInfo] = useState(null);
  const [assign, setAssign] = useState({ field_executive_id: "none", report_writer_id: "none" });
  const [assigning, setAssigning] = useState(false);
  const [dragJob, setDragJob] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [jobTab, setJobTab] = useState("bankformat");
  const [newBranch, setNewBranch] = useState(null); // {branch_name, address, email} when adding inline
  const [branchSaving, setBranchSaving] = useState(false);

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); api.get("/banks/empanelled").then((r) => setBankDirectory(r.data || [])).catch(() => {}); api.get("/report-formats").then((r) => setReportFormats(r.data || [])).catch(() => {}); api.get("/bank-templates/all").then((r) => setBankTpls(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (open) setJobTab("bankformat"); }, [open]);
  useEffect(() => {
    if (!params.get("new")) return;
    setEditId(null);
    const cid = params.get("client");
    if (cid) {
      api.get(`/clients/${cid}/profile`).then((r) => {
        const c = (r.data && r.data.client) || {};
        setForm({ ...EMPTY, client_id: cid, applicant_name: c.name || "", contact_mobile: c.phone || "",
          property_type: c.property_type || "Residential", property_address: c.property_address || "" });
      }).catch(() => setForm({ ...EMPTY, client_id: cid }));
    } else {
      setForm(EMPTY);
    }
    setOpen(true);
  }, [params]);
  useEffect(() => { const jid = params.get("job"); if (jid && jobs.length) { const j = jobs.find((x) => x.id === jid); if (j) openDetail(j); } }, [params, jobs]);
  useEffect(() => { const eid = params.get("edit"); if (eid && jobs.length) { const j = jobs.find((x) => x.id === eid); if (j) openEdit(j); } /* eslint-disable-next-line */ }, [params, jobs]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const nameOf = (id) => team.find((t) => t.id === id)?.name || "—";
  const createBranchInline = async () => {
    const name = (newBranch?.branch_name || "").trim();
    if (!name) { toast.error("Enter a branch name"); return; }
    if (!form.bank_name) { toast.error("Select a bank first"); return; }
    setBranchSaving(true);
    try {
      await api.post("/branches", { bank_name: form.bank_name, branch_name: name, address: newBranch.address || "", email: newBranch.email || "" });
      const dir = await api.get("/banks/empanelled").then((r) => r.data || []).catch(() => []);
      setBankDirectory(dir);
      setForm((f) => ({ ...f, branch_name: name, report_format_id: "" }));
      setNewBranch(null);
      toast.success(`Branch "${name}" added`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBranchSaving(false); }
  };

  const bankOptions = (() => {
    const names = bankDirectory.map((b) => b.bank_name);
    return (form.bank_name && !names.includes(form.bank_name)) ? [form.bank_name, ...names] : names;
  })();
  const branchOptions = (() => {
    const entry = bankDirectory.find((b) => b.bank_name === form.bank_name);
    const list = entry ? [...entry.branches] : [];
    if (form.branch_name && !list.includes(form.branch_name)) list.unshift(form.branch_name);
    return list;
  })();
  const branchFormats = (() => {
    const entry = bankDirectory.find((b) => b.bank_name === form.bank_name);
    const ids = (entry && entry.branch_formats && entry.branch_formats[form.branch_name]) || [];
    const byId = Object.fromEntries(reportFormats.map((f) => [f.id, f]));
    const assigned = ids.map((id) => byId[id]).filter(Boolean);
    const list = assigned.length > 0 ? assigned : reportFormats;
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  })();
  const usingAllFormatsFallback = (() => {
    const entry = bankDirectory.find((b) => b.bank_name === form.bank_name);
    const ids = (entry && entry.branch_formats && entry.branch_formats[form.branch_name]) || [];
    const byId = Object.fromEntries(reportFormats.map((f) => [f.id, f]));
    return ids.map((id) => byId[id]).filter(Boolean).length === 0 && reportFormats.length > 0;
  })();
  const jobBankTpl = (form.bank_name && form.report_format_id)
    ? bankTpls.find((t) => bankKey(t.bank_name) === bankKey(form.bank_name) && t.report_format_id === form.report_format_id)
    : null;
  const subjectCoords = (() => { const vd = form.valuation_details || {}; const lat = parseFloat(vd.field_lat ?? vd.latitude); const lon = parseFloat(vd.field_lon ?? vd.longitude); return (isFinite(lat) && isFinite(lon)) ? { lat, lon } : null; })();

  const pipeNext = (s) => JOB_STATUS_ORDER[JOB_STATUS_ORDER.indexOf(s) + 1];
  const advanceStage = async () => {
    const next = pipeNext(detail.status); if (!next) return;
    await updateJob(detail.id, { status: next }); setDetail((d) => ({ ...d, status: next }));
  };
  const sendDraft = async () => {
    try {
      const { data } = await api.post(`/jobs/${detail.id}/send-draft`);
      toast.success(data.emailed ? `Draft emailed to ${data.emailed}` : "Draft link ready (no client email on file)");
      setDetail((d) => ({ ...d, status: "sent_to_client" })); load();
      if (data.whatsapp_text) shareWhatsApp(detail.contact_mobile || "", data.whatsapp_text);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markApproved = async () => {
    try { await api.post(`/jobs/${detail.id}/client-approval`); toast.success("Marked client approved"); setDetail((d) => ({ ...d, status: "client_approved" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitForReview = async () => {
    try { await api.post(`/jobs/${detail.id}/submit-for-review`); toast.success("Sent to owner for review"); setDetail((d) => ({ ...d, status: "admin_review" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyApprovalLink = async () => {
    try { const { data } = await api.get(`/jobs/${detail.id}/client-links`); await navigator.clipboard.writeText(data.approval_link); toast.success("Approval link copied"); }
    catch { toast.error("Could not get link"); }
  };
  const releaseFromDetail = async () => {
    try { const data = await releaseReportAndInvoice(detail.id); toast.success(data.already_released ? `Invoice ${data.invoice.invoice_number} exists` : `Released · Invoice ${data.invoice.invoice_number}`); load(); setDetail(null); navigate("/app/invoices"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    const attachedTpl = (form.bank_name && form.report_format_id)
      ? bankTpls.find((t) => bankKey(t.bank_name) === bankKey(form.bank_name) && t.report_format_id === form.report_format_id)
      : null;
    const missing = [];
    if (!(form.bank_name || "").trim()) missing.push("Bank Name");
    if (!(form.applicant_name || "").trim()) missing.push("Applicant Name");
    if (!(form.property_address || "").trim()) missing.push("Property Address");
    if (missing.length) {
      setJobTab(!(form.bank_name || "").trim() ? "bankformat" : "basic");
      toast.error(`Please fill required field(s): ${missing.join(", ")}`);
      return;
    }
    if (form.site_visit_date && form.deadline && form.deadline < form.site_visit_date) {
      setJobTab("basic");
      toast.error("Deadline cannot be earlier than the Site Visit date.");
      return;
    }
    if (!editId && form.report_format_id && !attachedTpl) {
      toast.warning("No report template is attached for this bank & format. The job will still be created — ask the owner to attach it in Bank Branches.");
    }
    try {
      const payload = {
        ...form,
        loan_amount: parseFloat(form.loan_amount) || 0,
        fee: parseFloat(form.fee) || 0,
        tat_days: parseInt(form.tat_days, 10) || null,
        field_executive_id: form.field_executive_id === "none" ? null : form.field_executive_id,
        report_writer_id: form.report_writer_id === "none" ? null : form.report_writer_id,
        deadline: form.deadline || null,
        site_visit_date: form.site_visit_date || null,
      };
      let saved;
      if (editId) {
        ({ data: saved } = await api.put(`/jobs/${editId}?base_updated_at=${encodeURIComponent(baseUpdatedAt || "")}`, payload));
        toast.success("Saved — you can continue on the next tab");
      } else {
        ({ data: saved } = await api.post("/jobs", payload));
        toast.success("Job created — you can continue on the next tab");
      }
      // Keep the dialog open so the user can keep working across tabs. For a new job,
      // switch into edit mode so subsequent saves update the same record (no duplicates).
      if (!editId && saved?.id) setEditId(saved.id);
      if (saved?.updated_at) setBaseUpdatedAt(saved.updated_at);
      load();
    } catch (err) {
      if (err.response?.status === 409) { toast.error(err.response.data?.detail, { duration: 10000, action: { label: "Reload job", onClick: reloadEditingJob } }); return; }
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Re-open the currently edited job with fresh data (used after a concurrent-edit conflict).
  const reloadEditingJob = async () => {
    try {
      const { data } = await api.get("/jobs");
      const j = (Array.isArray(data) ? data : []).find((x) => x.id === editId);
      setJobs(Array.isArray(data) ? data : []);
      if (j) openEdit(j);
    } catch { /* ignore */ }
  };

  // Reviewer returns a job in Admin Review to the writer's prep queue with a note.
  const sendBackForRework = async () => {
    if (!reworkJob) return;
    try {
      await api.post(`/jobs/${reworkJob.id}/send-back`, { note: reworkNote });
      toast.success(`Sent back to ${reworkJob.report_writer_id ? nameOf(reworkJob.report_writer_id) : "the writer"} for rework`);
      setReworkJob(null); setReworkNote(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Persist the on-screen form to the DB before any report action, so the generated
  // PDF always reflects the latest entered details (reports are built from saved state).
  const persistForm = async () => {
    if (!editId) return true;
    try {
      const payload = {
        ...form,
        loan_amount: parseFloat(form.loan_amount) || 0,
        fee: parseFloat(form.fee) || 0,
        tat_days: parseInt(form.tat_days, 10) || null,
        field_executive_id: form.field_executive_id === "none" ? null : form.field_executive_id,
        report_writer_id: form.report_writer_id === "none" ? null : form.report_writer_id,
        deadline: form.deadline || null,
        site_visit_date: form.site_visit_date || null,
      };
      const { data: saved } = await api.put(`/jobs/${editId}?base_updated_at=${encodeURIComponent(baseUpdatedAt || "")}`, payload);
      if (saved?.updated_at) setBaseUpdatedAt(saved.updated_at);
      return true;
    } catch (err) {
      if (err.response?.status === 409) { toast.error(err.response.data?.detail, { duration: 10000, action: { label: "Reload job", onClick: reloadEditingJob } }); return false; }
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save latest details before generating.");
      return false;
    }
  };

  const openEdit = (job) => {
    setEditId(job.id);
    setForm({
      bank_name: job.bank_name || "", branch_name: job.branch_name || "", applicant_name: job.applicant_name || "",
      property_type: job.property_type || "Residential", property_address: job.property_address || "",
      property_location: job.property_location || "", contact_mobile: job.contact_mobile || "",
      loan_amount: job.loan_amount ?? "", fee: job.fee ?? "",
      field_executive_id: job.field_executive_id || "none", report_writer_id: job.report_writer_id || "none",
      status: job.status || "new", deadline: job.deadline ? job.deadline.slice(0, 10) : "", notes: job.notes || "",
      purpose: job.purpose || "Mortgage", bank_reference_no: job.bank_reference_no || "",
      site_visit_date: job.site_visit_date ? job.site_visit_date.slice(0, 10) : "", tat_days: job.tat_days ?? "",
      report_format_id: job.report_format_id || "",
      valuation_details: withDerived(job.valuation_details || {}),
    });
    setBaseUpdatedAt(job.updated_at || null);
    setReworkInfo(job.rework || null);
    setReportProcessed(!!job.report_processed);
    setListingsMeta(job.online_listings || null);
    setListingItems((job.online_listings && job.online_listings.items) || []);
    setDetail(null);
    setOpen(true);
  };

  const autoReview = (id) => {
    toast.info("AI is reviewing the draft against the uploaded documents…");
    api.post(`/jobs/${id}/ai-review`, {}, { timeout: 240000 })
      .then(({ data }) => { toast.success(`AI review done — ${(data.review?.discrepancies || []).length} observation(s). Draft invoice ${data.invoice?.invoice_number || "prepared"}.`); load(); })
      .catch((err) => { if (err.response?.status === 400) toast.message("AI review skipped — no documents uploaded on this job."); else toast.error(formatApiError(err.response?.data?.detail) || "AI review failed. Please try again."); });
  };
  const updateJob = async (id, patch) => {
    try { await api.put(`/jobs/${id}`, patch); load(); if (patch.status === "draft_prepared") autoReview(id); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const requestPrint = (id, reference) => {
    openValuationReport(id, reference);
  };
  const declGateOk = () => true;

  const patchVd = (p) => setForm((f) => {
    const vd = f.valuation_details || {};
    const next = { ...vd };
    Object.entries(p).forEach(([k, v]) => {
      if (v && typeof v === "object" && !Array.isArray(v) && (k === "essential_checks" || k === "doc_checklist")) {
        const cur = vd[k] || {};
        const merged = { ...cur };
        Object.entries(v).forEach(([ik, iv]) => { merged[ik] = { ...(cur[ik] || {}), ...iv }; });
        next[k] = merged;
      } else { next[k] = v; }
    });
    return { ...f, valuation_details: withDerived(next) };
  });

  useEffect(() => { if (open && editId) setCmpRadius(Number(form?.comparables_radius_km) || 2); /* eslint-disable-next-line */ }, [open, editId]);

  const setComparablesRadius = async (km) => {
    setCmpRadius(km);
    try { await api.put(`/jobs/${editId}/comparables-settings`, { radius_km: km }); toast.success(`Comparables radius set to ${km} km`); }
    catch { toast.error("Could not save radius"); }
  };

  const [refreshingListings, setRefreshingListings] = useState(false);
  const [listingsMeta, setListingsMeta] = useState(null);
  const [listingItems, setListingItems] = useState([]);
  const [photoPins, setPhotoPins] = useState([]);
  const [hiddenSources, setHiddenSources] = useState({});
  // Suggested rate band, unit-normalised to ₹/sq.ft (2bigha quotes ₹/sq.yd → ÷9), robust number parse, outlier-trimmed.
  const computeRateBand = (items) => {
    const toSqft = (r) => {
      const s = String(r || "");
      const m = s.match(/([0-9][0-9,]*(?:\.[0-9]+)?)/);
      if (!m) return null;
      let n = parseFloat(m[1].replace(/,/g, ""));
      if (!(n > 0)) return null;
      if (/sq\.?\s*yd|gaj|yard/i.test(s)) n = n / 9;
      else if (/acre/i.test(s)) n = n / 43560;
      return Math.round(n);
    };
    let nums = (items || []).map((l) => toSqft(l.rate)).filter((n) => n && n > 0).sort((a, b) => a - b);
    if (nums.length < 2) return null;
    const med0 = nums[Math.floor(nums.length / 2)];
    nums = nums.filter((n) => n >= med0 * 0.4 && n <= med0 * 2.5);
    if (nums.length < 2) return null;
    const mid = nums.length % 2 ? nums[(nums.length - 1) / 2] : Math.round((nums[nums.length / 2 - 1] + nums[nums.length / 2]) / 2);
    return { low: nums[0], mid, high: nums[nums.length - 1], count: nums.length };
  };
  useEffect(() => {
    if (open && editId) api.get(`/jobs/${editId}/photo-gps`).then((r) => setPhotoPins(r.data?.pins || [])).catch(() => setPhotoPins([]));
    else setPhotoPins([]);
  }, [open, editId]);
  const refreshListings = async () => {
    setRefreshingListings(true);
    try {
      if (!(await persistForm())) return;
      const { data } = await api.post(`/jobs/${editId}/refresh-listings`);
      setListingsMeta({ fetched_at: data.fetched_at, last_attempt_at: data.last_attempt_at, count: data.count });
      setListingItems(data.items || []);
      if (data.preserved) toast.info(data.note || "Portals are blocking new lookups — showing the last fetched listings.");
      else if (data.count > 0) toast.success(`${data.count} online listing(s) fetched within ${cmpRadius} km`);
      else toast.info(data.note || "No listings found in range");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshingListings(false); }
  };

  const setManualComps = (rows) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), manual_comparables: rows } }));
  const manualComps = (form.valuation_details || {}).manual_comparables || [];
  const addManualComp = () => setManualComps([...manualComps, { locality: "", area: "", rate: "", note: "", lat: "", lng: "" }]);
  const updateManualComp = (i, k, v) => setManualComps(manualComps.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeManualComp = (i) => setManualComps(manualComps.filter((_, idx) => idx !== i));
  const attachedComps = (form.valuation_details || {}).attached_comparables || [];
  const setAttachedComps = (rows) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), attached_comparables: rows } }));
  const toggleAttachedInclude = (i) => setAttachedComps(attachedComps.map((r, idx) => idx === i ? { ...r, include: !(r.include !== false) } : r));
  const removeAttached = (i) => setAttachedComps(attachedComps.filter((_, idx) => idx !== i));
  // Pre-fill a justification narrative from rate divergence + nearby comparable evidence.
  const buildJustificationDraft = () => {
    const vd = form.valuation_details || {};
    const G = parseFloat(vd.guideline_rate_per_sqft ?? (vd.rate_model || {}).guideline_rate) || 0;
    const M = parseFloat(vd.prevailing_market_rate) || 0;
    if (!(G > 0) || !(M > 0)) return "";
    const div = ((M - G) / G) * 100;
    const money = (x) => `₹${Math.round(x).toLocaleString("en-IN")}`;
    const toNum = (r) => { const n = parseFloat(String(r || "").replace(/,/g, "").match(/[0-9.]+/)?.[0] || ""); return n > 0 ? n : null; };
    const compRates = [
      ...attachedComps.filter((a) => a.include !== false).map((a) => toNum(a.rate)),
      ...(listingItems || []).filter((l) => !hiddenSources[l.source || "Online"]).map((l) => toNum(l.rate)),
      ...manualComps.map((m) => toNum(m.rate)),
    ].filter((n) => n && n > 0).sort((a, b) => a - b);
    const absDiv = Math.abs(div).toFixed(1);
    const lines = [];
    if (div >= 15) {
      lines.push(`• The adopted market rate of ${money(M)}/sq.ft is ${absDiv}% above the Collector guideline rate of ${money(G)}/sq.ft.`);
      lines.push("• The guideline rate is zone-averaged and periodically revised; it lags prevailing registered transactions in the vicinity, which have appreciated since the last revision.");
    } else if (div <= -15) {
      lines.push(`• The adopted market rate of ${money(M)}/sq.ft is ${absDiv}% below the Collector guideline rate of ${money(G)}/sq.ft.`);
      lines.push("• The lower adopted rate reflects site-specific constraints (e.g. irregular shape, limited frontage/access or subdued local demand) observed during inspection.");
    } else {
      lines.push(`• The adopted market rate of ${money(M)}/sq.ft is within ${absDiv}% of the Collector guideline rate of ${money(G)}/sq.ft and is consistent with prevailing market evidence.`);
    }
    if (compRates.length >= 2) {
      const mid = compRates[Math.floor(compRates.length / 2)];
      lines.push(`• Comparable evidence in the vicinity (${compRates.length} data points, ${money(compRates[0])}–${money(compRates[compRates.length - 1])}/sq.ft, median ≈ ${money(mid)}/sq.ft) supports the adopted rate — refer to the comparables annexure.`);
    } else {
      lines.push("• Recent registered comparable sales in the vicinity support the adopted rate — refer to the comparables annexure.");
    }
    lines.push("• (Draft — please review and edit before finalising.)");
    return lines.join("\n");
  };
  useEffect(() => {
    if (!open || jobTab !== "justify") return;
    if (((form.valuation_details || {}).rate_justification || "").trim()) return;
    const draft = buildJustificationDraft();
    if (draft) patchVd({ rate_justification: draft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTab, open]);

  const [geoBusy, setGeoBusy] = useState(null);
  const geocodeRow = async (i) => {
    const r = manualComps[i] || {};
    if (!(r.locality || "").trim()) { toast.error("Enter a locality / address first"); return; }
    const q = [r.locality, form.property_location, form.property_address].filter(Boolean).join(", ");
    setGeoBusy(i);
    try {
      const res = await geocodeAddress(q);
      if (res) {
        setForm((f) => {
          const rows = ((f.valuation_details || {}).manual_comparables || []).map((row, idx) => idx === i ? { ...row, lat: res.lat.toFixed(6), lng: res.lng.toFixed(6) } : row);
          return { ...f, valuation_details: { ...(f.valuation_details || {}), manual_comparables: rows } };
        });
        toast.success("Coordinates filled from locality");
      } else toast.error("Couldn't locate that address — enter lat/long manually");
    } catch { toast.error("Geocoding failed"); }
    finally { setGeoBusy(null); }
  };
  const [geoAllBusy, setGeoAllBusy] = useState(false);
  const geocodeAll = async () => {
    const targets = manualComps.map((r, i) => ({ r, i })).filter(({ r }) => (r.locality || "").trim() && !(r.lat && r.lng));
    if (!targets.length) { toast.info("Nothing to locate — add localities first (already-located rows are skipped)"); return; }
    setGeoAllBusy(true);
    let done = 0;
    for (const { r, i } of targets) {
      const q = [r.locality, form.property_location, form.property_address].filter(Boolean).join(", ");
      try {
        const res = await geocodeAddress(q);
        if (res) {
          setForm((f) => {
            const rows = ((f.valuation_details || {}).manual_comparables || []).map((row, idx) => idx === i ? { ...row, lat: res.lat.toFixed(6), lng: res.lng.toFixed(6) } : row);
            return { ...f, valuation_details: { ...(f.valuation_details || {}), manual_comparables: rows } };
          });
          done++;
        }
      } catch { /* skip row */ }
    }
    setGeoAllBusy(false);
    toast.success(`Located ${done} of ${targets.length} comparable(s)`);
  };
  const [locatingSubject, setLocatingSubject] = useState(false);
  const locateSubject = async () => {
    const q = [form.property_address, form.property_location].filter(Boolean).join(", ");
    if (!q.trim()) { toast.error("Enter the property address first (Basic tab)"); return; }
    setLocatingSubject(true);
    try {
      const res = await geocodeAddress(q);
      if (res) { patchVd({ field_lat: res.lat.toFixed(6), field_lon: res.lng.toFixed(6) }); toast.success("Subject plot located from address"); }
      else toast.error("Couldn't locate the property address — set it on the GIS tab map");
    } catch { toast.error("Geocoding failed"); }
    finally { setLocatingSubject(false); }
  };

  const processReportJob = async () => {
    if (!editId) return;
    setProcessing(true);
    const t = toast.loading("Processing report — compiling the full valuation…");
    try {
      if (!(await persistForm())) { toast.dismiss(t); return; }
      const data = await processReport(editId);
      setReportProcessed(true);
      toast.dismiss(t);
      const extra = data?.listings_count != null ? ` · ${data.listings_count} online listing(s)` : "";
      toast.success(`Report processed${extra}. Opening the final report…`);
      await openValuationReport(editId, form.reference);
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiError(err.response?.data?.detail) || "Could not process the report.");
    } finally {
      setProcessing(false);
    }
  };

  const releaseJob = async () => {
    if (!editId) return;
    if (!declGateOk()) return;
    setReleasing(true);
    try {
      if (!(await persistForm())) return;
      const data = await releaseReportAndInvoice(editId);
      if (data.already_released) toast.info(`Invoice ${data.invoice.invoice_number} already created for this job`);
      else toast.success(`Report released · Invoice ${data.invoice.invoice_number} created`);
      load();
      setOpen(false);
      navigate("/app/invoices");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReleasing(false); }
  };

  const runAiReview = async () => {
    if (!editId) return;
    setReviewing(true);
    try {
      if (!(await persistForm())) return;
      const { data } = await api.post(`/jobs/${editId}/ai-proofread`, {}, { timeout: 240000 });
      setReview(data);
      setReviewOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI review failed. Please try again.");
    } finally {
      setReviewing(false);
    }
  };

  const runAutoCorrect = async () => {
    if (!editId) return;
    setCorrecting(true);
    try {
      if (!(await persistForm())) return;
      const { data } = await api.post(`/jobs/${editId}/ai-autocorrect`);
      const list = (data.corrections || []).map((c) => ({ ...c, apply: true }));
      if (!list.length) { toast.info("No auto-correctable issues found — the report text looks clean."); return; }
      setCorrections(list);
      setReviewOpen(false);
      setCorrectOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Auto-correct failed. Please try again.");
    } finally {
      setCorrecting(false);
    }
  };

  const toggleCorr = (i) => setCorrections((cs) => cs.map((c, idx) => (idx === i ? { ...c, apply: !c.apply } : c)));

  const applyCorrections = async () => {
    const sel = corrections.filter((c) => c.apply);
    if (!sel.length) { toast.info("Select at least one correction to apply"); return; }
    const TOP = ["property_address", "applicant_name", "bank_name", "branch_name", "property_location", "notes"];
    const f = { ...form };
    const vd = { ...(f.valuation_details || {}) };
    sel.forEach((c) => { if (TOP.includes(c.key)) f[c.key] = c.corrected; else vd[c.key] = c.corrected; });
    f.valuation_details = vd;
    try {
      const payload = { ...f, loan_amount: parseFloat(f.loan_amount) || 0, fee: parseFloat(f.fee) || 0, tat_days: parseInt(f.tat_days, 10) || null, field_executive_id: f.field_executive_id === "none" ? null : f.field_executive_id, report_writer_id: f.report_writer_id === "none" ? null : f.report_writer_id, deadline: f.deadline || null, site_visit_date: f.site_visit_date || null };
      await api.put(`/jobs/${editId}`, payload);
      setForm(f);
      toast.success(`Applied ${sel.length} correction(s) to the report`);
      setCorrectOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not apply corrections");
    }
  };

  const uploadDoc = async (jobId, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/jobs/${jobId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached");
      fileEl.value = "";
      setDetail((d) => (d && d.id === jobId ? { ...d, documents: [...(d.documents || []), data] } : d));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteDoc = async (jobId, docId) => {
    try {
      await api.delete(`/jobs/${jobId}/documents/${docId}`);
      setDetail((d) => (d && d.id === jobId ? { ...d, documents: (d.documents || []).filter((x) => x.id !== docId) } : d));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addNote = async (jobId) => {
    const text = noteDraft.trim();
    if (!text && !notePhoto) return;
    try {
      let data;
      if (notePhoto) {
        const fd = new FormData();
        fd.append("text", text);
        fd.append("file", notePhoto);
        ({ data } = await api.post(`/jobs/${jobId}/notes/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post(`/jobs/${jobId}/notes`, { text }));
      }
      setDetail((d) => (d && d.id === jobId ? { ...d, notes_log: [...(d.notes_log || []), data] } : d));
      setNoteDraft("");
      setNotePhoto(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteNote = async (jobId, noteId) => {
    try {
      await api.delete(`/jobs/${jobId}/notes/${noteId}`);
      setDetail((d) => (d && d.id === jobId ? { ...d, notes_log: (d.notes_log || []).filter((n) => n.id !== noteId) } : d));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const dropOnStage = async (status) => {
    const job = dragJob;
    setDragJob(null);
    setDragOverCol(null);
    if (!job || job.status === status) return;
    setJobs((js) => js.map((j) => (j.id === job.id ? { ...j, status } : j)));
    try {
      await api.put(`/jobs/${job.id}`, { status });
      toast.success(`${job.reference} moved to ${JOB_STATUS_LABELS[status]}`);
      load();
      if (status === "draft_prepared") autoReview(job.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const openDetail = (job) => {    setDetail(job);
    setAssign({
      field_executive_id: job.field_executive_id || "none",
      report_writer_id: job.report_writer_id || "none",
    });
  };

  const assignChanged = !!detail && (
    assign.field_executive_id !== (detail.field_executive_id || "none")
    || assign.report_writer_id !== (detail.report_writer_id || "none")
  );

  const saveAssignment = async (jobId) => {
    setAssigning(true);
    try {
      const patch = {
        field_executive_id: assign.field_executive_id === "none" ? null : assign.field_executive_id,
        report_writer_id: assign.report_writer_id === "none" ? null : assign.report_writer_id,
      };
      const { data } = await api.put(`/jobs/${jobId}`, patch);
      setDetail(data);
      const named = [patch.field_executive_id && nameOf(patch.field_executive_id), patch.report_writer_id && nameOf(patch.report_writer_id)].filter(Boolean);
      toast.success(named.length ? `Assigned to ${named.join(" and ")} — notified by email` : "Assignment cleared");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAssigning(false); }
  };

  const fieldExecs = team.filter((t) => t.role === "field_executive" || t.role === "owner");
  const writers = team.filter((t) => t.role === "report_writer" || t.role === "owner");

  const ADMIN_REVIEW_IDX = JOB_STATUS_ORDER.indexOf("admin_review");
  const filtered = jobs.filter((j) => {
    // Once a staff member submits a job to Admin Review (or beyond), it leaves their
    // preparation queue — only the owner sees it from then on.
    if (!isOwner) {
      const si = JOB_STATUS_ORDER.indexOf(j.status);
      if (si >= ADMIN_REVIEW_IDX && ADMIN_REVIEW_IDX !== -1) return false;
    }
    const q = search.toLowerCase();
    const matchQ = !q || `${j.reference} ${(j.valuation_details && j.valuation_details.val_id) || ""} ${j.applicant_name} ${j.bank_name} ${j.property_address}`.toLowerCase().includes(q);
    const matchS = statusFilter === "all" || j.status === statusFilter;
    const matchB = bankFilter === "all" || `${j.bank_name}${j.branch_name ? ` — ${j.branch_name}` : ""}` === bankFilter;
    const matchV = valuerFilter === "all" || j.field_executive_id === valuerFilter || j.report_writer_id === valuerFilter;
    const matchP = ptFilter === "all" || j.property_type === ptFilter;
    const created = (j.created_at || "").slice(0, 10);
    const matchD = (!fromDate || created >= fromDate) && (!toDate || created <= toDate);
    return matchQ && matchS && matchB && matchV && matchP && matchD;
  });

  const bankFilterOptions = [...new Set(jobs.map((j) => j.bank_name && `${j.bank_name}${j.branch_name ? ` — ${j.branch_name}` : ""}`).filter(Boolean))].sort();
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Job Processing</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Valuation Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">{isOwner ? jobs.length : filtered.length} jobs{isOwner ? " · assign field executives & report writers" : " · prepare your assigned valuations"}</p>
        </div>
        {(isOwner || user?.role === "report_writer") && <Button onClick={() => { setEditId(null); setForm(EMPTY); setBaseUpdatedAt(null); setReworkInfo(null); setOpen(true); }} data-testid="add-job-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> {isOwner ? "New Job" : "Initiate Report"}</Button>}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, applicant, bank…" className="pl-9" data-testid="job-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{JOB_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{JOB_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={bankFilter} onValueChange={setBankFilter}>
          <SelectTrigger className="w-[190px]" data-testid="bank-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Bank Branches</SelectItem>{bankFilterOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={valuerFilter} onValueChange={setValuerFilter}>
          <SelectTrigger className="w-[160px]" data-testid="valuer-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Valuers</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={ptFilter} onValueChange={setPtFilter}>
          <SelectTrigger className="w-[170px]" data-testid="ptype-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Property Types</SelectItem>{PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <DatePicker value={fromDate} onChange={setFromDate} placeholder="From date" clearable className="w-[150px]" data-testid="from-date-filter" />
        <DatePicker value={toDate} onChange={setToDate} placeholder="To date" clearable className="w-[150px]" data-testid="to-date-filter" />
        <div className="flex rounded-full border border-border overflow-hidden">
          <button onClick={() => setLayout("list")} className={`px-3 py-2 text-xs font-medium transition-colors duration-150 ${layout === "list" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-muted"}`} data-testid="layout-list"><Rows size={15} /></button>
          <button onClick={() => setLayout("board")} className={`px-3 py-2 text-xs font-medium transition-colors duration-150 ${layout === "board" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-muted"}`} data-testid="layout-board"><Kanban size={15} /></button>
        </div>
      </div>

      {layout === "board" && (
        <div className="overflow-x-auto pb-2" data-testid="jobs-kanban">
          <p className="text-xs text-muted-foreground mb-2">Drag a card to another column to move the job to that stage.</p>
          <div className="flex gap-3 min-w-max">
            {JOB_STATUS_ORDER.map((s) => {
              const col = filtered.filter((j) => j.status === s);
              return (
                <div key={s} className="w-[260px] shrink-0" data-testid={`kanban-col-${s}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverCol(s); }}
                  onDragLeave={() => setDragOverCol((c) => (c === s ? null : c))}
                  onDrop={(e) => { e.preventDefault(); dropOnStage(s); }}>
                  <div className={`px-3 py-2 rounded-t-md border text-xs font-semibold uppercase tracking-wider flex items-center justify-between ${JOB_STATUS_COLORS[s]}`}>
                    <span>{JOB_STATUS_LABELS[s]}</span><span className="tabular">{col.length}</span>
                  </div>
                  <div className={`border border-t-0 border-border rounded-b-md p-2 space-y-2 min-h-[120px] transition-colors duration-150 ${dragOverCol === s && dragJob && dragJob.status !== s ? "bg-accent/10 ring-2 ring-accent/40" : "bg-muted/50"}`}>
                    {col.map((j) => {
                      const od = j.deadline && j.deadline.slice(0, 10) < today && j.status !== "completed";
                      return (
                        <div key={j.id} role="button" tabIndex={0} draggable
                          onDragStart={(e) => { setDragJob(j); e.dataTransfer.effectAllowed = "move"; }}
                          onDragEnd={() => { setDragJob(null); setDragOverCol(null); }}
                          onClick={() => openDetail(j)}
                          onKeyDown={(e) => { if (e.key === "Enter") openDetail(j); }}
                          data-testid={`kanban-card-${j.id}`}
                          className={`w-full text-left bg-white rounded-md border border-border p-3 cursor-grab active:cursor-grabbing transition-transform duration-150 hover:-translate-y-0.5 hover:shadow ${dragJob?.id === j.id ? "opacity-50" : ""}`}>
                          <div className="text-[10px] font-mono text-muted-foreground">{j.reference}</div>
                          {j.valuation_details && j.valuation_details.val_id && <div className="text-[10px] font-mono text-accent font-semibold">{j.valuation_details.val_id}</div>}
                          <div className="text-sm font-medium text-primary truncate mt-0.5">{j.applicant_name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{j.bank_name}</div>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-[10px] font-mono ${od ? "text-destructive font-semibold" : "text-muted-foreground"}`}>{j.deadline ? j.deadline.slice(0, 10) : "no deadline"}</span>
                            <span className="text-[11px] tabular text-primary">{inr(j.fee)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {col.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-4">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {layout === "list" && (
      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted sticky top-0">
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wide">Reference</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Applicant / Property</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Bank</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Field Exec</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Writer</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Deadline</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((j) => {
                const overdue = j.deadline && j.deadline.slice(0, 10) < today && j.status !== "completed";
                return (
                  <TableRow key={j.id} className="hover:bg-muted/40" data-testid={`job-row-${j.id}`}>
                    <TableCell className="font-mono text-xs"><button type="button" onClick={() => openEdit(j)} className="text-left text-primary hover:underline cursor-pointer" title="Open & edit" data-testid={`job-ref-open-${j.id}`}>{j.reference}{j.source === "public_form" && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-accent/20 text-accent">WEB</span>}{j.rework && JOB_STATUS_ORDER.indexOf(j.status) < ADMIN_REVIEW_IDX && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`job-rework-badge-${j.id}`}>REWORK</span>}{j.self_initiated && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200" title="Report writer initiated this job" data-testid={`job-selfinit-badge-${j.id}`}>SELF-INITIATED</span>}{j.valuation_details && j.valuation_details.val_id && <div className="text-[10px] text-accent font-semibold mt-0.5" data-testid={`job-valid-${j.id}`}>{j.valuation_details.val_id}</div>}</button></TableCell>
                    <TableCell className="max-w-[220px]"><div className="font-medium text-primary text-sm">{j.applicant_name}</div><div className="text-xs text-muted-foreground truncate">{j.property_type} · {j.property_address}</div></TableCell>
                    <TableCell className="text-sm">{j.bank_name}{j.branch_name && <div className="text-xs text-muted-foreground">{j.branch_name}</div>}</TableCell>
                    <TableCell>
                      {isOwner ? (
                        <Select value={j.field_executive_id || "none"} onValueChange={(v) => updateJob(j.id, { field_executive_id: v === "none" ? null : v })}>
                          <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`job-fe-${j.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">Unassigned</SelectItem>{fieldExecs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className="text-xs text-muted-foreground">{j.field_executive_id ? nameOf(j.field_executive_id) : "—"}</span>}
                    </TableCell>
                    <TableCell>
                      {isOwner ? (
                        <Select value={j.report_writer_id || "none"} onValueChange={(v) => updateJob(j.id, { report_writer_id: v === "none" ? null : v })}>
                          <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`job-rw-${j.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">Unassigned</SelectItem>{writers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className="text-xs text-muted-foreground">{j.report_writer_id ? nameOf(j.report_writer_id) : "—"}</span>}
                    </TableCell>
                    <TableCell><span className={`font-mono text-xs ${overdue ? "text-destructive font-semibold" : "text-muted-foreground"}`}>{j.deadline ? j.deadline.slice(0, 10) : "—"}</span></TableCell>
                    <TableCell>
                      <Select value={j.status} onValueChange={(v) => updateJob(j.id, { status: v })}>
                        <SelectTrigger className={`h-8 text-xs w-[140px] border ${JOB_STATUS_COLORS[j.status]}`} data-testid={`job-status-${j.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{JOB_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{JOB_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" title="Actions" data-testid={`job-actions-${j.id}`}><DotsThreeVertical size={18} weight="bold" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => openEdit(j)} data-testid={`edit-job-${j.id}`}><PencilSimple size={16} className="mr-2 text-accent" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => requestPrint(j.id, j.reference, j.valuation_details)} data-testid={`row-report-pdf-${j.id}`}><Printer size={16} className="mr-2 text-primary" /> Print report</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDetail(j)} data-testid={`timeline-job-${j.id}`}><ClockCounterClockwise size={16} className="mr-2 text-primary" /> Timeline</DropdownMenuItem>
                          {isOwner && j.status === "admin_review" && <DropdownMenuItem onClick={() => { setReworkJob(j); setReworkNote(""); }} data-testid={`rework-job-${j.id}`}><ArrowUUpLeft size={16} className="mr-2 text-amber-600" /> Send back for rework</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDelTarget(j)} className="text-destructive focus:text-destructive" data-testid={`delete-job-${j.id}`}><Trash size={16} className="mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">{(search || statusFilter !== "all" || bankFilter !== "all" || valuerFilter !== "all" || ptFilter !== "all" || fromDate || toDate) ? "No jobs match your filters." : "No jobs yet."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete job?</AlertDialogTitle><AlertDialogDescription>Remove {delTarget?.reference}? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { remove(delTarget.id); setDelTarget(null); }} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-job">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Valuation Progress</DialogTitle><DialogDescription>Contact, location, documents and status timeline for this job.</DialogDescription></DialogHeader>
          {detail && (
            <div data-testid="job-detail">
              <div className="border-b border-border pb-3 mb-4">
                <div className="font-mono text-xs text-muted-foreground">{detail.reference}</div>
                <div className="font-display text-lg font-semibold text-primary mt-0.5">{detail.applicant_name}</div>
                <div className="text-sm text-muted-foreground">{detail.property_type} · {detail.bank_name}{detail.branch_name ? ` — ${detail.branch_name}` : ""}</div>
                {detail.deadline && <div className="text-xs text-muted-foreground mt-1">Deadline: <span className="font-mono">{detail.deadline.slice(0, 10)}</span></div>}
                {!isOwner && JOB_STATUS_ORDER.indexOf(detail.status) >= ADMIN_REVIEW_IDX && <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800" data-testid="job-detail-lock"><b>Sent for review</b> — locked until the owner releases it.</div>}
                <div className="flex gap-2 mt-3">
                  {!(!isOwner && JOB_STATUS_ORDER.indexOf(detail.status) >= ADMIN_REVIEW_IDX) && <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => openEdit(detail)} data-testid="job-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit Job & Valuation</Button>}
                  <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => requestPrint(detail.id, detail.reference, detail.valuation_details)} data-testid="job-report-pdf-btn"><FileArrowDown size={14} className="mr-1" /> Valuation Report PDF</Button>
                </div>
              </div>

              <div className="mb-4" data-testid="job-pipeline">
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Pipeline</div>
                <div className="flex flex-wrap gap-1">
                  {JOB_STATUS_ORDER.map((s, i) => {
                    const cur = JOB_STATUS_ORDER.indexOf(detail.status);
                    const active = i === cur, done = i < cur;
                    return <span key={s} className={`text-[9px] px-1.5 py-0.5 rounded ${active ? "bg-accent text-accent-foreground font-bold" : done ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{JOB_STATUS_LABELS[s]}</span>;
                  })}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {pipeNext(detail.status) && <Button size="sm" className="rounded-full text-xs bg-primary hover:bg-[#113A64]" onClick={advanceStage} data-testid="job-advance-btn">Advance → {JOB_STATUS_LABELS[pipeNext(detail.status)]}</Button>}
                  {detail.status === "draft_prepared" && <Button size="sm" variant="outline" className="rounded-full text-xs border-primary text-primary hover:bg-primary/5" onClick={submitForReview} data-testid="job-submit-review-btn"><FileArrowDown size={14} className="mr-1" /> Send completed report to owner</Button>}
                  {["draft_prepared", "admin_review"].includes(detail.status) && <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={sendDraft} data-testid="job-send-draft-btn"><WhatsappLogo size={14} className="mr-1" /> Send draft to client</Button>}
                  {detail.status === "sent_to_client" && <>
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={markApproved} data-testid="job-mark-approved-btn">Mark client approved</Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={copyApprovalLink} data-testid="job-copy-approval-btn">Copy approval link</Button>
                  </>}
                  {isOwner && ["client_approved", "invoiced", "report_released"].includes(detail.status) && <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={releaseFromDetail} data-testid="job-release-btn"><FileArrowDown size={14} className="mr-1" /> Generate invoice & release</Button>}
                </div>
                {detail.client_approval?.status === "changes_requested" && <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="job-changes-note">Client requested changes: {detail.client_approval.message || "—"}</div>}
                {detail.client_approval?.status === "approved" && <div className="mt-2 text-xs text-teal-700" data-testid="job-approved-note">✓ Client approved {detail.client_approval.via === "client_link" ? "(via secure link)" : "(by staff)"}</div>}
              </div>

              {(detail.contact_mobile || detail.property_location) && (
                <div className="space-y-2 mb-4 text-sm">
                  {detail.contact_mobile && (
                    <div className="flex items-center gap-2" data-testid="job-detail-mobile">
                      <Phone size={16} weight="duotone" className="text-accent shrink-0" />
                      <span className="text-primary">{detail.contact_mobile}</span>
                      <a href={`tel:${detail.contact_mobile.replace(/[^0-9+]/g, "")}`} className="text-xs text-accent hover:underline ml-auto">Call</a>
                      <button className="text-xs text-accent hover:underline" onClick={() => shareWhatsApp(detail.contact_mobile, `Hello, regarding valuation ${detail.reference} for ${detail.applicant_name}.`)}>WhatsApp</button>
                    </div>
                  )}
                  {detail.property_location && (
                    <div className="flex items-center gap-2" data-testid="job-detail-location">
                      <MapPin size={16} weight="duotone" className="text-accent shrink-0" />
                      <span className="text-primary truncate">{detail.property_location}</span>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detail.property_location)}`} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline ml-auto whitespace-nowrap">Open in Maps</a>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground flex items-center gap-1.5"><Paperclip size={14} className="text-accent" /> Documents</div>
                  <label className="text-xs text-accent hover:underline cursor-pointer flex items-center gap-1" data-testid="job-doc-upload-label">
                    <UploadSimple size={14} /> Attach
                    <input type="file" className="hidden" data-testid="job-doc-input" onChange={(e) => uploadDoc(detail.id, e.target)} accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                  </label>
                </div>
                {(detail.documents || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No documents attached yet.</div>
                ) : (
                  <div className="space-y-1.5" data-testid="job-doc-list">
                    {(detail.documents || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-2 p-2 rounded-md bg-muted border border-border text-sm">
                        <FileArrowDown size={16} className="text-primary shrink-0" />
                        <a href={`${API}/jobs/${detail.id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1" data-testid={`job-doc-${d.id}`}>{d.filename}</a>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{Math.max(1, Math.round((d.size || 0) / 1024))} KB</span>
                        <button className="text-destructive shrink-0" onClick={() => deleteDoc(detail.id, d.id)} data-testid={`job-doc-delete-${d.id}`}><Trash size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isOwner && <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3" data-testid="job-assign-panel">
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><UserPlus size={14} className="text-accent" /> Assign Team</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Field Executive</Label>
                    <Select value={assign.field_executive_id} onValueChange={(v) => setAssign((a) => ({ ...a, field_executive_id: v }))}>
                      <SelectTrigger className="h-9 text-xs" data-testid="job-assign-field-exec"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{fieldExecs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Report Writer</Label>
                    <Select value={assign.report_writer_id} onValueChange={(v) => setAssign((a) => ({ ...a, report_writer_id: v }))}>
                      <SelectTrigger className="h-9 text-xs" data-testid="job-assign-report-writer"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{writers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button size="sm" className="w-full rounded-full mt-3 bg-primary hover:bg-[#113A64]" disabled={assigning || !assignChanged} onClick={() => saveAssignment(detail.id)} data-testid="job-assign-btn">
                  <UserPlus size={14} className="mr-1" /> {assigning ? "Assigning…" : assignChanged ? "Assign & Notify by Email" : "Assigned"}
                </Button>
                <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="job-assign-current">
                  Currently: {detail.field_executive_id ? nameOf(detail.field_executive_id) : "no field executive"} · {detail.report_writer_id ? nameOf(detail.report_writer_id) : "no report writer"}
                </p>
              </div>}

              <PhotoGallery job={detail} />

              <FieldData job={detail} onChange={load} />

              <div className="mb-4">
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Site Notes</div>
                <div className="space-y-1.5 mb-2">
                  {(detail.notes_log || []).map((n) => (
                    <div key={n.id} className="text-xs bg-muted rounded-md p-2" data-testid={`job-note-${n.id}`}>
                      {n.text && <div className="text-primary whitespace-pre-wrap">{n.text}</div>}
                      {n.photo && (
                        <a href={`${API}/jobs/${detail.id}/notes/${n.id}/photo`} target="_blank" rel="noreferrer" data-testid={`job-note-photo-view-${n.id}`}>
                          <img src={`${API}/jobs/${detail.id}/notes/${n.id}/photo`} alt="site" className="mt-1 rounded-md max-h-44 border border-border" />
                        </a>
                      )}
                      <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
                        <span>{n.by_name} · {new Date(n.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                        <button onClick={() => deleteNote(detail.id, n.id)} className="text-destructive hover:underline" data-testid={`job-note-del-${n.id}`}>Delete</button>
                      </div>
                    </div>
                  ))}
                  {(detail.notes_log || []).length === 0 && <div className="text-xs text-muted-foreground">No site notes yet.</div>}
                </div>
                <div className="flex gap-2 items-center">
                  <Input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(detail.id); } }} placeholder="Add a site note…" className="text-xs h-9" data-testid="job-note-input" />
                  <label className="shrink-0 cursor-pointer h-9 w-9 rounded-full bg-muted flex items-center justify-center text-primary hover:bg-muted/70 transition-colors" title="Attach photo" data-testid="job-note-photo">
                    <Camera size={16} />
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setNotePhoto(e.target.files?.[0] || null)} />
                  </label>
                  <Button size="sm" variant="outline" className="h-9 rounded-full text-xs shrink-0" onClick={() => addNote(detail.id)} data-testid="job-note-add">Add</Button>
                </div>
                {notePhoto && <div className="text-[10px] text-accent mt-1 inline-flex items-center gap-1" data-testid="job-note-photo-name"><Camera size={11} /> {notePhoto.name} attached</div>}
              </div>

              <JobTimeline status={detail.status} statusHistory={detail.status_history} />
              <Button variant="outline" className="w-full rounded-full mt-5" data-testid="job-whatsapp-btn"
                onClick={() => shareWhatsApp("", `*Valuation update* from Aakriti Associates\nRef: ${detail.reference}\nApplicant: ${detail.applicant_name}\nStatus: ${JOB_STATUS_LABELS[detail.status]}\nTrack live: ${window.location.origin}/track`)}>
                <WhatsappLogo size={16} className="mr-1" /> Share status on WhatsApp
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reworkJob} onOpenChange={(o) => { if (!o) { setReworkJob(null); setReworkNote(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Send back for rework</DialogTitle>
            <DialogDescription>{reworkJob?.reference} will return to {reworkJob ? (reworkJob.report_writer_id ? nameOf(reworkJob.report_writer_id) : "the assigned writer") : ""}'s preparation list for editing.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs">What needs to be fixed?</Label>
          <Textarea value={reworkNote} onChange={(e) => setReworkNote(e.target.value)} placeholder="e.g. Correct the guideline rate and re-check the boundary schedule" rows={4} data-testid="rework-note-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setReworkJob(null); setReworkNote(""); }}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-[#113A64]" onClick={sendBackForRework} data-testid="rework-confirm-btn"><ArrowUUpLeft size={16} className="mr-1" /> Send back for rework</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setJobTab("bankformat"); if (!o) { setEditId(null); const fc = params.get("from"); if (fc) navigate(`/app/clients/${fc}`); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit Valuation Job" : "New Valuation Job"}</DialogTitle>
            <DialogDescription>Enter property, contact and assignment details.</DialogDescription>
          </DialogHeader>
          {reworkInfo && editId && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="rework-banner">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-700">Sent back for rework{reworkInfo.by ? ` by ${reworkInfo.by}` : ""}{reworkInfo.at ? ` · ${String(reworkInfo.at).slice(0, 10)}` : ""}</div>
              <div className="text-sm text-amber-900 mt-1">{reworkInfo.note || "Please review and resubmit."}</div>
            </div>
          )}
          <form onSubmit={save} className="space-y-3" data-testid="job-form">
            <Tabs value={jobTab} onValueChange={setJobTab}>
              <TabsList className={`grid ${editId ? "grid-cols-10" : "grid-cols-8"} w-full gap-1`}>
                <TabsTrigger value="bankformat" data-testid="tab-bankformat" className="text-xs px-1 min-w-0 truncate">Bank</TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents" className="text-xs px-1 min-w-0 truncate">Docs</TabsTrigger>
                <TabsTrigger value="checklist" data-testid="tab-checklist" className="text-xs px-1 min-w-0 truncate">Checklist</TabsTrigger>
                <TabsTrigger value="basic" data-testid="tab-basic" className="text-xs px-1 min-w-0 truncate">Basic</TabsTrigger>
                <TabsTrigger value="valuation" data-testid="tab-valuation" className="text-xs px-1 min-w-0 truncate">Valuation</TabsTrigger>
                <TabsTrigger value="ratemodel" data-testid="tab-ratemodel" className="text-xs px-1 min-w-0 truncate">Rate</TabsTrigger>
                <TabsTrigger value="justify" data-testid="tab-justify" className="text-xs px-1 min-w-0 truncate">Justify</TabsTrigger>
                <TabsTrigger value="gis" data-testid="tab-gis" className="text-xs px-1 min-w-0 truncate">GIS</TabsTrigger>
                {editId && <TabsTrigger value="ratemap" data-testid="tab-ratemap" className="text-xs px-1 min-w-0 truncate">Map</TabsTrigger>}
                {editId && <TabsTrigger value="report" data-testid="tab-report" className="text-xs px-1 min-w-0 truncate">Report</TabsTrigger>}
              </TabsList>
              <TabsContent value="bankformat" className="space-y-3 mt-4">
            <p className="text-xs text-muted-foreground -mb-1">Start here — choose the bank branch and the report format. Formats are filtered to the selected branch, and the chosen format sets the report layout (e.g. Open Land skips the building section).</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bank Name *</Label>
                <Select value={form.bank_name || ""} onValueChange={(v) => setForm({ ...form, bank_name: v, branch_name: "", report_format_id: "" })}>
                  <SelectTrigger data-testid="job-form-bank"><SelectValue placeholder="Select empanelled bank" /></SelectTrigger>
                  <SelectContent>{bankOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between min-h-[20px]"><Label>Branch</Label>
                  {form.bank_name && <button type="button" onClick={() => setNewBranch(newBranch ? null : { branch_name: "", address: "", email: "" })} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="job-add-branch-toggle"><Plus size={12} />{newBranch ? "Close" : "New branch"}</button>}
                </div>
                <Select value={form.branch_name || ""} onValueChange={(v) => setForm({ ...form, branch_name: v, report_format_id: "" })} disabled={!form.bank_name}>
                  <SelectTrigger data-testid="job-form-branch"><SelectValue placeholder={form.bank_name ? "Select branch" : "Select bank first"} /></SelectTrigger>
                  <SelectContent>{branchOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {newBranch && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="job-new-branch-panel">
                <p className="text-xs font-semibold text-primary">Add a new branch for {form.bank_name}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Branch name *" value={newBranch.branch_name} onChange={(e) => setNewBranch({ ...newBranch, branch_name: e.target.value })} data-testid="job-new-branch-name" />
                  <Input placeholder="Branch email (optional)" value={newBranch.email} onChange={(e) => setNewBranch({ ...newBranch, email: e.target.value })} data-testid="job-new-branch-email" />
                </div>
                <Input placeholder="Address (optional)" value={newBranch.address} onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })} data-testid="job-new-branch-address" />
                <div className="flex justify-end gap-2">
                  <Button type="button" size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setNewBranch(null)} data-testid="job-new-branch-cancel">Cancel</Button>
                  <Button type="button" size="sm" className="rounded-full bg-primary hover:bg-[#113A64] text-xs" disabled={branchSaving} onClick={createBranchInline} data-testid="job-new-branch-save">{branchSaving ? "Adding…" : "Add branch"}</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">You can add full branch details later in Bank Branches.</p>
              </div>
            )}
            <div className="space-y-1.5"><Label>Report Format</Label>
              <Select value={form.branch_name ? (form.report_format_id || "none") : undefined} onValueChange={(v) => setForm({ ...form, report_format_id: v === "none" ? "" : v })} disabled={!form.branch_name}>
                <SelectTrigger data-testid="job-form-format"><SelectValue placeholder={form.branch_name ? "Select report format" : "Select branch first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific format</SelectItem>
                  {branchFormats.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} · {LAYOUT_LABELS[f.layout_key] || f.layout_key}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.branch_name && usingAllFormatsFallback && <p className="text-[11px] text-muted-foreground">No formats ticked for this branch yet — showing all formats. Tick the branch's formats in Bank Branches to narrow this list.</p>}
              {form.report_format_id && (
                jobBankTpl ? (
                  <a href={`${API}/bank-templates/template?bank=${encodeURIComponent(form.bank_name)}&format_id=${form.report_format_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-primary hover:underline" data-testid="job-bank-template-link">
                    <Paperclip size={14} /> Bank template attached — download “{jobBankTpl.template_filename}”
                  </a>
                ) : (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="job-bank-template-missing">⚠ No bank template attached for this bank &amp; format. The job can still be created; ask the owner to attach it in Bank Branches.</p>
                )
              )}
              {(() => {
                const sel = reportFormats.find((f) => f.id === form.report_format_id);
                if (!sel) return null;
                const hasTpl = sel.template_path || sel.template_filename;
                return hasTpl ? (
                  <a href={`${API}/report-formats/${sel.id}/template`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-primary hover:underline" data-testid="format-preview-link">
                    <Eye size={14} /> Preview “{sel.name}” layout
                  </a>
                ) : (
                  <p className="text-[11px] text-muted-foreground mt-1">No sample template uploaded for “{sel.name}” yet.</p>
                );
              })()}
            </div>
            <div className="flex justify-end pt-1">
              <Button type="button" size="sm" className="rounded-full bg-primary hover:bg-[#113A64] text-xs" disabled={!form.bank_name} onClick={() => setJobTab("documents")} data-testid="bankformat-continue">Continue to Docs</Button>
            </div>
              </TabsContent>
              <TabsContent value="basic" className="space-y-3 mt-4">
            <div className="space-y-1.5"><Label>Applicant Name *</Label><Input required value={form.applicant_name} onChange={set("applicant_name")} data-testid="job-form-applicant" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Property Type</Label>
                <Select value={form.property_type} onValueChange={set("property_type")}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Loan Amount (₹)</Label><Input type="number" value={form.loan_amount} onChange={set("loan_amount")} /></div>
              {isOwner && <div className="space-y-1.5"><Label>Professional Fee (₹)</Label><Input type="number" value={form.fee} onChange={set("fee")} data-testid="job-form-fee" /></div>}
            </div>
            <div className="space-y-1.5"><Label>Property Address *</Label><Textarea required value={form.property_address} onChange={set("property_address")} rows={2} data-testid="job-form-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Property Location</Label><Input value={form.property_location} onChange={set("property_location")} placeholder="Area / landmark / Maps link" data-testid="job-form-location" /></div>
              <div className="space-y-1.5"><Label>Contact Mobile No(s)</Label><Input value={form.contact_mobile} onChange={set("contact_mobile")} placeholder="e.g. 98261 XXXXX" data-testid="job-form-mobile" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {isOwner && <div className="space-y-1.5"><Label>Field Executive</Label>
                <Select value={form.field_executive_id} onValueChange={set("field_executive_id")}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{fieldExecs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({ROLE_LABELS[t.role]})</SelectItem>)}</SelectContent></Select>
              </div>}
              {isOwner && <div className="space-y-1.5"><Label>Report Writer</Label>
                <Select value={form.report_writer_id} onValueChange={set("report_writer_id")}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{writers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({ROLE_LABELS[t.role]})</SelectItem>)}</SelectContent></Select>
              </div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Purpose of Valuation</Label>
                <Select value={form.purpose} onValueChange={set("purpose")}><SelectTrigger data-testid="job-form-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent>{JOB_PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Bank Reference No.</Label><Input value={form.bank_reference_no} onChange={set("bank_reference_no")} data-testid="job-form-bankref" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Site Visit Date</Label><DatePicker value={form.site_visit_date} onChange={set("site_visit_date")} placeholder="Select date" clearable className="w-full" data-testid="job-form-visit" /></div>
              <div className="space-y-1.5"><Label>Deadline</Label><DatePicker value={form.deadline} onChange={set("deadline")} placeholder="Select date" clearable className="w-full" data-testid="job-form-deadline" /></div>
              <div className="space-y-1.5"><Label>TAT (days)</Label><Input type="number" value={form.tat_days} onChange={set("tat_days")} placeholder="3" data-testid="job-form-tat" /></div>
            </div>
            <div className="space-y-1.5"><Label>Fee Quotation (₹)</Label><Input type="number" value={form.fee} onChange={set("fee")} data-testid="job-form-fee" /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
              </TabsContent>
              <TabsContent value="valuation" className="mt-4">
                <ValuationDetailsForm value={form.valuation_details || {}} layout={(reportFormats.find((f) => f.id === form.report_format_id) || {}).layout_key} onChange={(vd) => setForm((f) => ({ ...f, valuation_details: vd }))} />
              </TabsContent>
              <TabsContent value="documents" className="mt-4">
                <DocumentsTab
                  jobId={editId}
                  vd={form.valuation_details || {}}
                  onContinue={() => setJobTab("basic")}
                  onEnsureJob={async () => {
                    if (editId) return editId;
                    try {
                      const payload = {
                        ...form,
                        bank_name: (form.bank_name || "").trim() || "Draft",
                        applicant_name: (form.applicant_name || "").trim() || "Draft Applicant",
                        property_address: (form.property_address || "").trim() || "To be completed",
                        loan_amount: parseFloat(form.loan_amount) || 0, fee: parseFloat(form.fee) || 0,
                        tat_days: parseInt(form.tat_days, 10) || null,
                        field_executive_id: form.field_executive_id === "none" ? null : form.field_executive_id,
                        report_writer_id: form.report_writer_id === "none" ? null : form.report_writer_id,
                        deadline: form.deadline || null, site_visit_date: form.site_visit_date || null,
                      };
                      const { data } = await api.post("/jobs", payload);
                      setEditId(data.id);
                      load();
                      toast.success("Draft job created & VAL ID assigned — upload and read documents, then complete the Basic tab.");
                      return data.id;
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return null; }
                  }}
                  onCoords={(patch) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), ...patch } }))}
                  value={(form.valuation_details || {}).doc_observations || {}}
                  onChange={(obs) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), doc_observations: obs } }))}
                  onApplyFields={(fields) => setForm((f) => {
                    const patch = {};
                    Object.entries(fields || {}).forEach(([k, v]) => { if (v != null && String(v).trim()) patch[k] = v; });
                    return { ...f, valuation_details: withDerived({ ...(f.valuation_details || {}), ...patch }) };
                  })}
                  onApplyBasic={(basic) => setForm((f) => {
                    const SENTINELS = new Set(["draft", "draft applicant", "to be completed"]);
                    const patch = {};
                    Object.entries(basic || {}).forEach(([k, v]) => {
                      const cur = String(f[k] || "").trim().toLowerCase();
                      if (v != null && String(v).trim() && (!cur || SENTINELS.has(cur))) patch[k] = v;
                    });
                    return { ...f, ...patch };
                  })}
                  onApproveRefresh={async (approvedObs) => {
                    const vdNew = { ...(form.valuation_details || {}), doc_observations: approvedObs };
                    const payload = {
                      ...form, valuation_details: vdNew,
                      loan_amount: parseFloat(form.loan_amount) || 0, fee: parseFloat(form.fee) || 0,
                      tat_days: parseInt(form.tat_days, 10) || null,
                      field_executive_id: form.field_executive_id === "none" ? null : form.field_executive_id,
                      report_writer_id: form.report_writer_id === "none" ? null : form.report_writer_id,
                      deadline: form.deadline || null, site_visit_date: form.site_visit_date || null,
                    };
                    try {
                      await api.put(`/jobs/${editId}`, payload);
                      setForm((f) => ({ ...f, valuation_details: vdNew }));
                      toast.success("Approved & saved — opening report");
                      openValuationReport(editId);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }} />
              </TabsContent>
              <TabsContent value="checklist" className="mt-4">
                <DocsChecklistTab
                  jobId={editId}
                  reference={form.bank_reference_no || editId}
                  onPersist={editId ? persistForm : undefined}
                  value={(form.valuation_details || {}).doc_checklist || {}}
                  onChange={(cl) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), doc_checklist: cl } }))} />
              </TabsContent>
              <TabsContent value="gis" className="mt-4">
                <p className="text-xs text-muted-foreground mb-3">Overlay Khasra / Master-Plan / Satellite maps to a common scale using two known points, trace the plot, and the aligned overlay + measured area are added to this job's valuation report PDF.</p>
                <GisOverlayStudio
                  bankName={form.bank_name}
                  branchName={form.branch_name}
                  comparables={(form.valuation_details || {}).comparable_sales}
                  coords={(() => { const vd = form.valuation_details || {}; const lat = parseFloat(vd.field_lat ?? vd.latitude); const lon = parseFloat(vd.field_lon ?? vd.longitude); return (isFinite(lat) && isFinite(lon)) ? { lat, lon } : null; })()}
                  value={(form.valuation_details || {}).gis_overlay || {}}
                  onChange={(g) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), gis_overlay: g } }))}
                  onApplyArea={(sqft) => { setForm((f) => ({ ...f, valuation_details: withDerived({ ...(f.valuation_details || {}), land_area_sqft: sqft }) })); toast.success(`Land area set to ${sqft.toLocaleString("en-IN")} sq.ft from GIS measurement`); }} />
              </TabsContent>
              {editId && (
              <TabsContent value="ratemap" className="mt-4 space-y-3">
                <div>
                  <div className="font-display text-base font-semibold text-primary">Comparable Rate Map</div>
                  <p className="text-xs text-muted-foreground mt-0.5">Subject plot and nearby sale comparables plotted by latitude/longitude, to review prevailing land rates. Online listings appear after you refresh them in the Report tab; add lat/long to a manual comparable to plot it here too.</p>
                </div>
                {(() => {
                  const srcs = [...new Set((listingItems || []).map((l) => l.source || "Online"))];
                  if (srcs.length === 0) return null;
                  return (
                    <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="map-source-filter">
                      <span className="text-muted-foreground">Sources:</span>
                      {srcs.map((s) => (
                        <label key={s} className="inline-flex items-center gap-1.5 cursor-pointer" data-testid={`map-source-toggle-${s}`}>
                          <input type="checkbox" checked={!hiddenSources[s]} onChange={() => setHiddenSources((h) => ({ ...h, [s]: !h[s] }))} className="accent-accent" />
                          <span className="text-primary">{s}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
                {(() => {
                  const b = computeRateBand((listingItems || []).filter((l) => !hiddenSources[l.source || "Online"]));
                  if (!b) return null;
                  const f = (n) => `₹${n.toLocaleString("en-IN")}`;
                  return (
                    <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-xs" data-testid="rate-band-suggestion">
                      <span className="font-semibold text-primary">Suggested rate band</span> <span className="text-muted-foreground">(from {b.count} nearby comps, ₹/sq.ft)</span> — Low <span className="font-semibold">{f(b.low)}</span> · Median <span className="font-semibold text-accent">{f(b.mid)}</span> · High <span className="font-semibold">{f(b.high)}</span>
                    </div>
                  );
                })()}
                <ComparablesMap
                  subject={(() => { const vd = form.valuation_details || {}; const lat = parseFloat(vd.field_lat ?? vd.latitude); const lon = parseFloat(vd.field_lon ?? vd.longitude); return (isFinite(lat) && isFinite(lon)) ? { lat, lon, rate: vd.prevailing_market_rate ? `₹${Number(vd.prevailing_market_rate).toLocaleString("en-IN")}/sq.ft` : "", area: vd.land_area_sqft ? `${Number(vd.land_area_sqft).toLocaleString("en-IN")} sq.ft` : "" } : null; })()}
                  listings={(listingItems || []).filter((l) => !hiddenSources[l.source || "Online"])}
                  manual={manualComps}
                  photoPins={photoPins}
                />
                {(() => { const vd = form.valuation_details || {}; const hasCoords = isFinite(parseFloat(vd.field_lat ?? vd.latitude)) && isFinite(parseFloat(vd.field_lon ?? vd.longitude)); return (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button type="button" size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={locatingSubject} onClick={locateSubject} data-testid="locate-subject-btn"><MapPin size={13} className="mr-1" />{locatingSubject ? "Locating…" : (hasCoords ? "Re-locate subject from address" : "Locate subject from address")}</Button>
                    {!hasCoords && <span className="text-[11px] text-amber-700" data-testid="ratemap-no-coords">⚠ No subject coordinates yet — locate it or add lat/long in the Valuation tab.</span>}
                  </div>
                ); })()}
                {attachedComps.length > 0 && (
                  <div className="rounded-lg border border-border p-4 space-y-2" data-testid="attached-comparables-panel">
                    <div>
                      <div className="font-display text-sm font-semibold text-primary">Attached historical comparables</div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Attached from the Historical Valuation Map. Rows marked “In report” (and their rate-trend graph) print in the comparables annexure.</p>
                    </div>
                    <div className="divide-y divide-border">
                      {attachedComps.map((a, i) => { const inc = a.include !== false; return (
                        <div key={a.id || i} className="flex items-center gap-3 py-2" data-testid={`attached-comp-${i}`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-primary truncate">{a.reference || a.applicant_name || "—"}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{a.bank_name || ""}{a.property_type ? ` · ${a.property_type}` : ""}{a.date ? ` · ${a.date}` : ""}{a.distance_km != null ? ` · ${a.distance_km} km` : ""}</div>
                          </div>
                          <div className="text-sm font-semibold text-primary shrink-0">{a.rate ? `₹${Number(a.rate).toLocaleString("en-IN")}/sqft` : "—"}</div>
                          <button type="button" onClick={() => toggleAttachedInclude(i)} data-testid={`attached-include-${i}`} className={`px-3 py-1 rounded-full text-xs font-semibold border shrink-0 transition-colors ${inc ? "bg-emerald-600 text-white border-emerald-600" : "bg-transparent text-muted-foreground border-border hover:border-emerald-400"}`}>{inc ? "In report" : "Excluded"}</button>
                          <button type="button" onClick={() => removeAttached(i)} title="Remove comparable" data-testid={`attached-remove-${i}`} className="shrink-0 text-muted-foreground hover:text-red-600"><Trash size={15} /></button>
                        </div> ); })}
                    </div>
                  </div>
                )}
              </TabsContent>
              )}
              <TabsContent value="ratemodel" className="mt-4 space-y-4">
                {(() => {
                  const b = computeRateBand((listingItems || []).filter((l) => !hiddenSources[l.source || "Online"]));
                  if (!b) return null;
                  const f = (n) => `₹${n.toLocaleString("en-IN")}`;
                  return (
                    <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="ratemodel-rate-band">
                      <div className="text-xs"><span className="font-semibold text-primary">Suggested rate band</span> <span className="text-muted-foreground">(from {b.count} nearby comps, ₹/sq.ft)</span><br />Low <span className="font-semibold">{f(b.low)}</span> · Median <span className="font-semibold text-accent">{f(b.mid)}</span> · High <span className="font-semibold">{f(b.high)}</span></div>
                      <Button type="button" size="sm" className="rounded-full bg-primary hover:bg-[#113A64] text-xs" data-testid="adopt-median-btn" onClick={() => { setForm((fm) => ({ ...fm, valuation_details: withDerived({ ...(fm.valuation_details || {}), prevailing_market_rate: b.mid }) })); toast.success(`Adopted median rate ${f(b.mid)}/sq.ft`); }}>Adopt median {f(b.mid)}</Button>
                    </div>
                  );
                })()}
                <RateModelTab
                  value={(form.valuation_details || {}).rate_model || {}}
                  source={(form.valuation_details || {}).guideline_source || {}}
                  adopted={(form.valuation_details || {}).prevailing_market_rate}
                  onPatchVd={patchVd}
                  onChange={(rm) => setForm((f) => ({ ...f, valuation_details: { ...(f.valuation_details || {}), rate_model: rm } }))}
                  onAdopt={(rate) => setForm((f) => ({ ...f, valuation_details: withDerived({ ...(f.valuation_details || {}), prevailing_market_rate: Math.round(rate) }) }))} />
              </TabsContent>
              <TabsContent value="justify" className="mt-4 space-y-4">
                {(() => {
                  const vd = form.valuation_details || {};
                  const G = parseFloat(vd.guideline_rate_per_sqft ?? (vd.rate_model || {}).guideline_rate) || 0;
                  const M = parseFloat(vd.prevailing_market_rate) || 0;
                  const div = G > 0 && M > 0 ? ((M - G) / G) * 100 : null;
                  const needed = div != null && Math.abs(div) >= 15;
                  const money = (x) => `₹${Math.round(x).toLocaleString("en-IN")}`;
                  const PRESETS = [
                    "The Collector guideline rate lags prevailing market transactions, which have appreciated since the last revision.",
                    "The subject enjoys superior road frontage / corner location commanding a premium over the guideline.",
                    "Recent registered comparable sales in the vicinity support the adopted rate (see comparables annexure).",
                    "The locality has seen rapid development / infrastructure growth, raising market rates above guideline.",
                    "The guideline rate is zone-averaged; the subject's specific location warrants an upward adjustment.",
                    "Market rate is below guideline due to litigation / irregular shape / limited demand for this plot.",
                  ];
                  const append = (t) => patchVd({ rate_justification: (vd.rate_justification || "") + (vd.rate_justification ? "\n" : "") + "• " + t });
                  return (
                    <>
                      <p className="text-xs text-muted-foreground">Where the adopted market rate differs materially from the Collector guideline rate, record the valuer's justification here. It is recommended when the divergence exceeds 15%.</p>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="rounded-lg border border-border p-3"><div className="text-[11px] text-muted-foreground">Guideline rate (G)</div><div className="font-semibold text-primary" data-testid="justify-g">{G ? money(G) + "/sq.ft" : "—"}</div></div>
                        <div className="rounded-lg border border-border p-3"><div className="text-[11px] text-muted-foreground">Adopted market rate</div><div className="font-semibold text-accent" data-testid="justify-m">{M ? money(M) + "/sq.ft" : "—"}</div></div>
                        <div className="rounded-lg border border-border p-3"><div className="text-[11px] text-muted-foreground">Divergence</div><div className={`font-semibold ${needed ? "text-amber-700" : "text-primary"}`} data-testid="justify-divergence">{div != null ? `${div > 0 ? "+" : ""}${div.toFixed(1)}%` : "—"}</div></div>
                      </div>
                      {div == null && <p className="text-xs text-muted-foreground" data-testid="justify-setrates">Set the Guideline rate (Rate tab) and the adopted market rate to see whether a written justification is required.</p>}
                      {needed && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="justify-needed">⚠ The adopted rate diverges {Math.abs(div).toFixed(1)}% from the guideline rate — a written justification is recommended for the report.</div>}
                      {div != null && !needed && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="justify-ok">The adopted rate is within 15% of the guideline rate; a justification is optional.</div>}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <Label>Justification of land rate adopted</Label>
                          <button type="button" onClick={() => { const d = buildJustificationDraft(); if (d) { patchVd({ rate_justification: d }); toast.success("Draft generated — review and edit"); } else { toast.info("Set the guideline and adopted rates first"); } }} data-testid="justify-autodraft-btn" className="text-[11px] px-2 py-1 rounded-full border border-accent/40 text-accent hover:bg-accent/10 transition-colors shrink-0">Auto-draft from rates & comps</button>
                        </div>
                        <Textarea rows={6} value={vd.rate_justification || ""} onChange={(e) => patchVd({ rate_justification: e.target.value })} placeholder="Explain why the adopted market rate differs from the Collector guideline rate…" data-testid="justify-text" />
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="text-[11px] text-muted-foreground self-center">Insert:</span>
                        {PRESETS.map((t, i) => <button type="button" key={i} onClick={() => append(t)} data-testid={`justify-preset-${i}`} className="text-[10px] px-2 py-1 rounded-full border bg-background hover:border-primary hover:bg-accent/10 transition-colors text-left max-w-[260px] truncate" title={t}>{t.slice(0, 44)}…</button>)}
                      </div>
                      <label className="flex items-center gap-2 text-xs font-medium rounded-lg border bg-muted/30 p-2.5 cursor-pointer" data-testid="justify-include-row">
                        <Checkbox checked={vd.rate_justification_include !== false} onCheckedChange={(v) => patchVd({ rate_justification_include: v === true })} data-testid="justify-include-toggle" />
                        Include this justification in the PDF report
                      </label>
                    </>
                  );
                })()}
              </TabsContent>
              {editId && (
              <TabsContent value="report" className="mt-4 space-y-4">
                <div className="rounded-lg border border-border p-4 space-y-3" data-testid="site-photos-panel">
                  <div>
                    <div className="font-display text-base font-semibold text-primary">Site Photographs</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Arrange the photos that print on the cover and the site-photographs annexure.</p>
                  </div>
                  <SitePhotosManager jobId={editId} />
                </div>
                <div className="rounded-lg border border-border p-4 space-y-3" data-testid="declarations-panel">
                  <div>
                    <div className="font-display text-base font-semibold text-primary">Declarations (Annexure-I)</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Answer each undertaking Yes or No — these print in Annexure-I. Any item left unanswered prints as Yes.</p>
                  </div>
                  <div className="space-y-2">
                    {DECL_UNDERTAKINGS.map((s, idx) => {
                      const n = idx + 1;
                      const val = (form.valuation_details || {})[`decl_${n}`] || "Yes";
                      return (
                        <div key={n} className="flex items-start gap-3 justify-between border-b border-border/60 pb-2 last:border-0">
                          <p className="text-xs leading-snug flex-1"><span className="font-semibold text-primary mr-1">({String.fromCharCode(96 + n)})</span>{s}</p>
                          <div className="flex gap-1 shrink-0" data-testid={`decl-answer-${n}`}>
                            <button type="button" onClick={() => patchVd({ [`decl_${n}`]: "Yes" })} data-testid={`decl-yes-${n}`} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${val === "Yes" ? "bg-emerald-600 text-white border-emerald-600" : "bg-transparent text-muted-foreground border-border hover:border-emerald-400"}`}>Yes</button>
                            <button type="button" onClick={() => patchVd({ [`decl_${n}`]: "No" })} data-testid={`decl-no-${n}`} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${val === "No" ? "bg-rose-600 text-white border-rose-600" : "bg-transparent text-muted-foreground border-border hover:border-rose-400"}`}>No</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-4 space-y-3" data-testid="bg-info-panel">
                  <div>
                    <div className="font-display text-base font-semibold text-primary">Background Information &amp; Valuer Comments</div>
                    <p className="text-xs text-muted-foreground mt-0.5">These comments print in Annexure-I of the report. Leave any blank to show “—”.</p>
                  </div>
                  <div className="space-y-3">
                    {BG_PARTICULARS.map((p, i) => {
                      const n = i + 1;
                      return (
                        <div key={n} className="space-y-1">
                          <Label className="text-xs leading-snug font-medium">{n}. {p}</Label>
                          <Textarea rows={2} value={(form.valuation_details || {})[`bg_comment_${n}`] || ""} onChange={(e) => patchVd({ [`bg_comment_${n}`]: e.target.value })} placeholder="Valuer comment…" data-testid={`bg-comment-${n}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-4 space-y-3" data-testid="generate-report-panel">
                  {(() => { const vd = form.valuation_details || {}; const pe = vd.parts_enabled || {}; const names = { A: "Land", B: "Building", C: "Extra Items", D: "Amenities", E: "Misc", F: "Services" }; const layout = (reportFormats.find((f) => f.id === form.report_format_id) || {}).layout_key; const off = Object.keys(names).filter((L) => pe[L] === false || (layout === "open_land" && ["B", "C", "D", "E"].includes(L) && pe[L] !== true)); const hideEmpty = vd.hide_empty_parts; return (off.length || hideEmpty) ? (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="report-parts-hint">
                      {off.map((L) => <span key={L} className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold" data-testid={`report-part-off-${L}`}>{names[L]} off</span>)}
                      {hideEmpty && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Empty parts auto-skipped</span>}
                      <span className="text-muted-foreground">— excluded from the report (set in the Valuation tab)</span>
                    </div>
                  ) : null; })()}
                  <div>
                    <div className="font-display text-base font-semibold text-primary">Generate Report</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Review the draft, then issue the final report and raise the invoice.</p>
                  </div>
                  <DeviationSignoff vd={form.valuation_details || {}} onPatch={patchVd} />
                  <div className="flex items-center gap-2 flex-wrap" data-testid="comparables-radius">
                    <span className="text-xs text-muted-foreground">Comparables radius:</span>
                    {[1, 2, 3].map((km) => (
                      <button key={km} type="button" onClick={() => setComparablesRadius(km)} data-testid={`cmp-radius-${km}`}
                        className={`h-7 px-3 rounded-full text-xs border transition-colors ${cmpRadius === km ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>{km} km</button>
                    ))}
                    <Button type="button" size="sm" variant="outline" className="rounded-full h-7 text-xs ml-auto" disabled={refreshingListings} onClick={refreshListings} data-testid="refresh-listings-btn">{refreshingListings ? "Fetching…" : "Refresh online listings"}</Button>
                  </div>
                  {listingsMeta && (listingsMeta.fetched_at || listingsMeta.last_attempt_at) && (
                    <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="listings-freshness">
                      {listingsMeta.fetched_at && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-muted-foreground" title={absTime(listingsMeta.fetched_at)} data-testid="listings-updated-badge">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Updated {relTime(listingsMeta.fetched_at)} · {absTime(listingsMeta.fetched_at)}
                        </span>
                      )}
                      {listingsMeta.last_attempt_at && listingsMeta.last_attempt_at !== listingsMeta.fetched_at && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-muted-foreground" title={absTime(listingsMeta.last_attempt_at)} data-testid="listings-tried-badge">
                          <ClockCounterClockwise size={11} /> Last tried {relTime(listingsMeta.last_attempt_at)} · {absTime(listingsMeta.last_attempt_at)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="rounded-lg border border-dashed border-border p-3 space-y-2" data-testid="manual-comparables-panel">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-primary">Manual comparables</div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Nearby sale rates you've gathered locally — always printed in the report alongside online listings.</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {manualComps.some((r) => (r.locality || "").trim() && !(r.lat && r.lng)) && <Button type="button" size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={geoAllBusy} onClick={geocodeAll} data-testid="geocode-all-btn"><MapPin size={13} className="mr-1" />{geoAllBusy ? "Locating…" : "Locate all"}</Button>}
                        <Button type="button" size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={addManualComp} data-testid="add-manual-comp-btn"><Plus size={13} className="mr-1" /> Add row</Button>
                      </div>
                    </div>
                    {manualComps.length > 0 && (
                      <div className="space-y-2">
                        {manualComps.map((r, i) => {
                          const rlat = parseFloat(r.lat), rlon = parseFloat(r.lng);
                          const dist = (subjectCoords && isFinite(rlat) && isFinite(rlon)) ? `${haversineKm(subjectCoords.lat, subjectCoords.lon, rlat, rlon).toFixed(2)} km ${compassDir(subjectCoords.lat, subjectCoords.lon, rlat, rlon)} of subject` : null;
                          return (
                          <div key={i} className="space-y-0.5">
                            <div className="flex flex-wrap gap-2 items-center" data-testid={`manual-comp-row-${i}`}>
                            <Input className="h-8 text-xs flex-1 min-w-[140px]" placeholder="Locality / address" value={r.locality || ""} onChange={(e) => updateManualComp(i, "locality", e.target.value)} data-testid={`manual-comp-locality-${i}`} />
                            <Input className="h-8 text-xs w-20" placeholder="Area sq.ft" value={r.area || ""} onChange={(e) => updateManualComp(i, "area", e.target.value)} data-testid={`manual-comp-area-${i}`} />
                            <Input className="h-8 text-xs w-24" placeholder="₹/sq.ft" value={r.rate || ""} onChange={(e) => updateManualComp(i, "rate", e.target.value)} data-testid={`manual-comp-rate-${i}`} />
                            <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground shrink-0">Lat</span><Input className="h-8 text-xs w-20" placeholder="Latitude" value={r.lat || ""} onChange={(e) => updateManualComp(i, "lat", e.target.value)} data-testid={`manual-comp-lat-${i}`} /></div>
                            <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground shrink-0">Lng</span><Input className="h-8 text-xs w-20" placeholder="Longitude" value={r.lng || ""} onChange={(e) => updateManualComp(i, "lng", e.target.value)} data-testid={`manual-comp-lng-${i}`} /></div>
                            <Input className="h-8 text-xs flex-1 min-w-[120px]" placeholder="Source / note" value={r.note || ""} onChange={(e) => updateManualComp(i, "note", e.target.value)} data-testid={`manual-comp-note-${i}`} />
                            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary" title="Auto-fill lat/long from locality" disabled={geoBusy === i} onClick={() => geocodeRow(i)} data-testid={`manual-comp-geocode-${i}`}><MapPin size={14} /></Button>
                            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-red-600" onClick={() => removeManualComp(i)} data-testid={`manual-comp-remove-${i}`}><Trash size={14} /></Button>
                            </div>
                            {dist && <p className="text-[10px] text-muted-foreground pl-1" data-testid={`manual-comp-distance-${i}`}>Distance: {dist}</p>}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-border pt-3">
                    <Button type="button" variant="outline" disabled={reviewing} className="rounded-full w-full border-accent/40 text-accent hover:bg-accent/10" onClick={runAiReview} data-testid="ai-review-btn">
                      <Sparkle size={16} className="mr-2" /> {reviewing ? "Reviewing with Gemini AI…" : "Transcribe & Review with Gemini AI"}
                    </Button>
                    <p className="text-[11px] text-muted-foreground mt-2">Runs an overall Gemini AI proofread of the whole report — spelling, logical consistency, and presentation/illustration suggestions before you release it.</p>
                  </div>
                  <div className="border-t border-border pt-3 space-y-2">
                    <Button type="button" variant="outline" className="rounded-full w-full border-primary/40 text-primary hover:bg-primary/5" onClick={async () => { if (await persistForm()) toast.success("All changes saved — ready to re-process"); }} data-testid="report-save-changes-btn"><FileArrowDown size={16} className="mr-2 rotate-180" /> Save Changes</Button>
                    <Button type="button" disabled={processing} className="rounded-full bg-accent text-white hover:bg-accent/90 w-full" onClick={processReportJob} data-testid="process-report-btn">{processing ? "Processing…" : (reportProcessed ? "Re-process Report (rebuild full report)" : "Process Report (generate full report)")}</Button>
                    <div className="grid gap-2 border-t border-border pt-2 mt-1">
                      {jobBankTpl && (
                        <a href={`${API}/bank-templates/template?bank=${encodeURIComponent(form.bank_name)}&format_id=${form.report_format_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10" data-testid="report-bank-template-btn"><Paperclip size={16} /> Download attached bank template ({jobBankTpl.template_filename})</a>
                      )}
                      <Button type="button" variant="outline" className="rounded-full justify-start" onClick={async () => { if (await persistForm()) openValuationReport(editId, form.reference, { draft: true }); }} data-testid="report-draft-btn"><FileArrowDown size={16} className="mr-2 text-muted-foreground" /> Download Draft (watermarked, no letterhead)</Button>
                      <Button type="button" variant="outline" className="rounded-full justify-start" onClick={async () => { if (!declGateOk()) return; if (await persistForm()) requestPrint(editId, form.reference, form.valuation_details); }} data-testid="report-final-btn"><FileArrowDown size={16} className="mr-2 text-primary" /> Download Final Report (with letterhead)</Button>
                      {isOwner && <Button type="button" variant="outline" className="rounded-full justify-start" onClick={async () => { if (!declGateOk()) return; if (await persistForm()) emailValuationReport(editId); }} data-testid="report-email-btn"><EnvelopeSimple size={16} className="mr-2 text-accent" /> Email Final Report to Bank</Button>}
                      <p className="text-[11px] text-muted-foreground">Your latest entries are saved automatically before the report is generated, so the PDF always matches what you've filled in.</p>
                    </div>
                    {isOwner && <Button type="button" disabled={releasing || !reportProcessed} className="rounded-full bg-primary hover:bg-[#113A64] w-full disabled:opacity-50" onClick={releaseJob} data-testid="release-report-btn">{releasing ? "Releasing…" : "Release Report & Generate Invoice"}</Button>}
                    {isOwner && <p className="text-[11px] text-muted-foreground mt-2">{reportProcessed ? "Releasing creates a draft invoice pre-filled with this job's client and reference (plus a Razorpay payment link when online payments are enabled), which you can edit before sending." : "Process the report first — this compiles the full valuation and opens the final PDF. Release unlocks once it has been processed."}</p>}
                  </div>
                </div>
              </TabsContent>
              )}
            </Tabs>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-job-btn">{editId ? "Save Changes" : "Create Job"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkle size={18} className="text-accent" /> Gemini AI Report Review</DialogTitle>
            <DialogDescription>Overall proofread of the report — spelling, logic and presentation. Suggestions only; nothing is changed automatically.</DialogDescription>
          </DialogHeader>
          {review && (
            <div className="space-y-4 text-sm">
              {review.overall && (
                <div className="rounded-lg bg-muted/50 border border-border p-3" data-testid="ai-review-overall">
                  <div className="font-semibold text-primary mb-1">Overall assessment</div>
                  <p className="text-muted-foreground">{review.overall}</p>
                </div>
              )}
              {[
                { key: "spelling", title: "Spelling & grammar", icon: "✍️", get: (x) => ({ main: x.text, sub: x.suggestion, where: x.where }) },
                { key: "logic", title: "Logical consistency", icon: "🔎", get: (x) => ({ main: x.issue, sub: "", where: x.where }) },
                { key: "illustration", title: "Presentation & illustration", icon: "🎨", get: (x) => ({ main: x.suggestion, sub: "", where: x.where }) },
              ].map((sec) => {
                const items = Array.isArray(review[sec.key]) ? review[sec.key] : [];
                return (
                  <div key={sec.key} data-testid={`ai-review-${sec.key}`}>
                    <div className="font-semibold text-primary mb-1.5">{sec.title} <span className="text-xs font-normal text-muted-foreground">({items.length})</span></div>
                    {items.length === 0 ? (
                      <p className="text-xs text-muted-foreground pl-1">No issues found.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {items.map((it, idx) => {
                          const v = sec.get(it);
                          return (
                            <li key={idx} className="rounded-md border border-border p-2 text-xs">
                              <div className="text-foreground">{v.main}</div>
                              {v.sub ? <div className="text-accent mt-0.5">→ {v.sub}</div> : null}
                              {v.where ? <div className="text-muted-foreground mt-0.5">in: {v.where}</div> : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
              <p className="text-[10px] text-muted-foreground text-right">Generated by {review.model || "Gemini"}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" className="rounded-full bg-accent text-white hover:bg-accent/90" disabled={correcting} onClick={runAutoCorrect} data-testid="ai-autocorrect-btn">
              <Sparkle size={15} className="mr-1.5" /> {correcting ? "Preparing fixes…" : "Auto-correct Report"}
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setReviewOpen(false)} data-testid="ai-review-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-correct-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkle size={18} className="text-accent" /> Auto-correct Report</DialogTitle>
            <DialogDescription>Review the suggested text fixes below and apply the ones you approve. Only spelling, grammar and place-name corrections are proposed — numbers, rates, areas and dates are never changed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <button type="button" className="text-xs text-accent hover:underline" onClick={() => setCorrections((cs) => cs.map((c) => ({ ...c, apply: true })))} data-testid="corr-select-all">Select all</button>
              <span className="text-xs text-muted-foreground">{corrections.filter((c) => c.apply).length} of {corrections.length} selected</span>
            </div>
            {corrections.map((c, i) => (
              <label key={i} className="flex gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-muted/40" data-testid={`corr-row-${i}`}>
                <input type="checkbox" className="mt-1 accent-current text-accent" checked={!!c.apply} onChange={() => toggleCorr(i)} data-testid={`corr-check-${i}`} />
                <div className="flex-1 text-xs">
                  <div className="text-red-600 line-through">{c.current}</div>
                  <div className="text-green-700 font-medium">{c.corrected}</div>
                  {c.reason ? <div className="text-muted-foreground mt-0.5">{c.reason}</div> : null}
                  <div className="text-muted-foreground mt-0.5">in: {c.section}{c.key ? ` · ${c.key}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" className="rounded-full bg-primary hover:bg-[#113A64]" onClick={applyCorrections} data-testid="corr-apply-btn">Apply {corrections.filter((c) => c.apply).length} correction(s)</Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setCorrectOpen(false)} data-testid="corr-cancel-btn">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
